import { Component, computed, effect, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { Table, TableModule } from 'primeng/table';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RatingModule } from 'primeng/rating';
import { RippleModule } from 'primeng/ripple';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { TagModule } from 'primeng/tag';

import { DropdownModule } from 'primeng/dropdown';
import { ChartModule } from 'primeng/chart';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectIsLoading, selectUserHistoryPredictions } from '../../store/selectors/prediction.selectors';
import { PredictionService, RoiData } from '../../services/prediction.service';
import { SkeletonModule } from 'primeng/skeleton';



@Component({
    selector: 'app-history',
    imports: [TableModule,
        MultiSelectModule,
        SelectModule,
        InputIconModule,
        DropdownModule,
        TagModule,
        InputTextModule,
        SliderModule,
        ProgressBarModule,
        ToggleButtonModule,
        ToastModule,
        CommonModule,
        ChartModule,
        FormsModule,
        ButtonModule,
        RatingModule,
        RippleModule,
        SkeletonModule,
        IconFieldModule],
    templateUrl: './history.component.html',
    styleUrl: './history.component.scss'
})
export class HistoryComponent implements OnInit {
    private store = inject(Store);
    private predictionService = inject(PredictionService);
    userHistoryPredictions = toSignal(this.store.select(selectUserHistoryPredictions))
    isLoading = toSignal(this.store.select(selectIsLoading))
    sortedUserHistoryPredictions = computed(() => {
        const predictions = this.userHistoryPredictions() ?? [];
        return [...predictions].sort(
            (a, b) =>
                new Date(b.event_date).getTime() -
                new Date(a.event_date).getTime()
        );
    });
    test = effect(() => {
        console.log("history", this.sortedUserHistoryPredictions())
    })

    roiData = signal<RoiData | null>(null);
    isLoadingRoi = signal<boolean>(false);
    selectedPeriod = signal<number>(30);

    roiChartData: any;
    chartOptions: any;
    competition: any[] = [];
    activityValues: number[] = [0, 100];
    lastUpdate = new Date().toLocaleString('fr-FR');



    periodOptions = [
        { label: '7 derniers jours', value: 7 },
        { label: '30 derniers jours', value: 30 },
        { label: '90 derniers jours', value: 90 },
        { label: '1 an', value: 365 },
        { label: 'Tout', value: 3650 },
    ];

    @ViewChild('filter') filter!: ElementRef;

    constructor() {
        effect(() => {
            const period = this.selectedPeriod();
            if (period) {
                this.loadRoiData(period);
            }
        });

        effect(() => {
            const data = this.roiData();
            if (data) {
                this.updateChartData(data);
            }
        });
    }

    ngOnInit() {
        this.initializeStaticData();
        this.initializeChartOptions();
        this.loadRoiData(30);
    }

    private initializeStaticData() {
        this.competition = [
            { label: 'UEFA Champions League', value: 'UEFA Champions League' },
            { label: 'UEFA Europa League', value: 'UEFA Europa League' },
            { label: 'Premier League', value: 'Premier League' },
            { label: 'Ligue 1', value: 'Ligue 1' },
            { label: 'NBA', value: 'NBA' }
        ];
    }

    private initializeChartOptions() {
        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,

            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        usePointStyle: true,
                        color: '#495057'
                    }
                },
                title: {
                    display: true,
                    text: 'Comparaison des performances ROI',
                    font: {
                        size: 16,
                        weight: 'bold'
                    },
                    color: '#495057'
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        label: function (context: any) {
                            return context.dataset.label + ': ' + context.parsed.y.toFixed(2) + '%';
                        }
                    }
                }
            },
            scales: {
                x: {
                    display: true,
                    title: {
                        display: true,
                        text: 'Période',
                        color: '#6c757d'
                    },
                    grid: {
                        color: '#e9ecef'
                    },
                    ticks: {
                        color: '#6c757d'
                    }
                },
                y: {
                    display: true,
                    title: {
                        display: true,
                        text: 'ROI (%)',
                        color: '#6c757d'
                    },
                    grid: {
                        color: '#e9ecef'
                    },
                    ticks: {
                        color: '#6c757d',
                        callback: function (value: any) {
                            return value + '%';
                        }
                    },
                    beginAtZero: true
                }
            },
            interaction: {
                mode: 'nearest' as const,
                axis: 'x' as const,
                intersect: false
            }
        };
    }

    private loadRoiData(periodDays: number) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user.user_id) return;

        this.isLoadingRoi.set(true);

        this.predictionService.getCombinedRoi(user.user_id, periodDays).subscribe({
            next: (data) => {
                this.roiData.set(data);
                this.isLoadingRoi.set(false);
            },
            error: (error) => {
                console.error('Erreur lors du chargement des données ROI:', error);
                this.isLoadingRoi.set(false);
                this.setDefaultChartData();
            }
        });
    }

    private updateChartData(data: RoiData) {
        this.roiChartData = {
            labels: data.labels,
            datasets: [
                {
                    label: 'ROI Coteo',
                    data: data.platform_roi,
                    fill: false,
                    borderColor: '#42A5F5',
                    backgroundColor: '#42A5F5',
                    tension: 0.4,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBackgroundColor: '#42A5F5',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    pointHoverBackgroundColor: '#42A5F5',
                    pointHoverBorderColor: '#ffffff'
                },
                {
                    label: 'Votre ROI',
                    data: data.user_roi,
                    fill: false,
                    borderColor: '#FFA726',
                    backgroundColor: '#FFA726',
                    tension: 0.4,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    spanGaps: true,
                    pointBackgroundColor: '#FFA726',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    pointHoverBackgroundColor: '#FFA726',
                    pointHoverBorderColor: '#ffffff'
                }
            ]
        };
    }

    private setDefaultChartData() {
        this.roiChartData = {
            labels: ['Pas de données'],
            datasets: [
                {
                    label: 'ROI Coteo',
                    data: [0],
                    fill: false,
                    borderColor: '#42A5F5',
                    tension: 0.4
                },
                {
                    label: 'Votre ROI',
                    data: [0],
                    fill: false,
                    borderColor: '#FFA726',
                    tension: 0.4
                }
            ]
        };
    }

    onPeriodChange(event: any) {
        this.selectedPeriod.set(event.value);
    }

    refreshRoiData() {
        this.loadRoiData(this.selectedPeriod());
    }

    getAverageUserRoi(): number {
        const data = this.roiData();
        if (!data || data.user_roi.length === 0) return 0;

        const sum = data.user_roi.reduce((acc, val) => acc + val, 0);
        return Math.round((sum / data.user_roi.length) * 100) / 100;
    }

    getAveragePlatformRoi(): number {
        const data = this.roiData();
        if (!data || data.platform_roi.length === 0) return 0;

        const sum = data.platform_roi.reduce((acc, val) => acc + val, 0);
        return Math.round((sum / data.platform_roi.length) * 100) / 100;
    }

    getPerformanceDifference(): number {
        return Math.abs(this.getAverageUserRoi() - this.getAveragePlatformRoi());
    }

    getPerformanceStatus(): 'excellent' | 'good' | 'average' | 'poor' {
        const diff = this.getPerformanceDifference();
        if (diff >= 20) return 'excellent';
        if (diff >= 5) return 'good';
        if (diff >= -5) return 'average';
        return 'poor';
    }

    getPerformanceColor(): string {
        const status = this.getPerformanceStatus();
        switch (status) {
            case 'excellent': return 'text-green-600';
            case 'good': return 'text-blue-600';
            case 'average': return 'text-yellow-600';
            case 'poor': return 'text-red-600';
            default: return 'text-gray-600';
        }
    }

    getPerformanceIcon(): string {
        const status = this.getPerformanceStatus();
        switch (status) {
            case 'excellent': return 'pi-arrow-up';
            case 'good': return 'pi-arrow-up';
            case 'average': return 'pi-minus';
            case 'poor': return 'pi-arrow-down';
            default: return 'pi-minus';
        }
    }

    clear(table: Table) {
        table.clear();
        this.filter.nativeElement.value = '';
    }



    options = [
        { label: 'Ligue 1', value: 'Ligue 1' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Completed', value: 'COMPLETED' }
    ];
}
